/**
 * Draft Storage Service
 * 
 * Manages persistence of draft messages, reply context, and edit state
 * to sessionStorage for the TI-M Matrix WebApp.
 * 
 * Storage Schema:
 * {
 *   "tim_draft_{conversationId}": {
 *     "text": "Draft message text",
 *     "timestamp": 1234567890123,
 *     "replyTo": {
 *       "id": "messageId",
 *       "eventId": "$eventId",
 *       "senderName": "John Doe",
 *       "content": "Original message"
 *     },
 *     "editing": {
 *       "id": "messageId",
 *       "eventId": "$eventId",
 *       "originalContent": "Being edited"
 *     }
 *   }
 * }
 */

import logger from '../utils/logger';

class DraftStorageService {
  constructor(storageKeyPrefix = 'tim_draft') {
    this.storageKeyPrefix = storageKeyPrefix;
    this.maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    this.storageAvailable = this.checkStorageAvailability();
  }

  /**
   * Check if sessionStorage is available and working
   */
  checkStorageAvailability() {
    try {
      const testKey = '__tim_storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (e) {
      logger.warn('SessionStorage not available, drafts will only persist in memory:', e);
      return false;
    }
  }

  /**
   * Generate storage key for a conversation
   */
  getStorageKey(conversationId) {
    return `${this.storageKeyPrefix}_${conversationId}`;
  }

  /**
   * Save draft for a conversation
   * @param {string} conversationId - The conversation/room ID
   * @param {Object|string} draftData - Draft data (can be string for text-only or object)
   * @returns {boolean} Success status
   */
  saveDraft(conversationId, draftData) {
    if (!this.storageAvailable) {
      logger.debug('DraftStorage: Storage not available, skipping save');
      return false;
    }

    if (!conversationId) {
      logger.warn('DraftStorage: Cannot save draft without conversationId');
      return false;
    }

    try {
      // Normalize draftData to object format
      const normalizedData = typeof draftData === 'string' 
        ? { text: draftData }
        : draftData;

      // Don't save empty drafts
      if (!normalizedData.text && !normalizedData.replyTo && !normalizedData.editing) {
        this.clearDraft(conversationId);
        return true;
      }

      const draftToSave = {
        ...normalizedData,
        timestamp: Date.now(),
      };

      const storageKey = this.getStorageKey(conversationId);
      sessionStorage.setItem(storageKey, JSON.stringify(draftToSave));
      
      logger.debug(`DraftStorage: Saved draft for ${conversationId}`, draftToSave);
      return true;
    } catch (e) {
      // Handle quota exceeded or other errors
      if (e.name === 'QuotaExceededError') {
        logger.error('DraftStorage: Storage quota exceeded, cannot save draft');
      } else {
        logger.error('DraftStorage: Error saving draft:', e);
      }
      return false;
    }
  }

  /**
   * Get draft for a conversation
   * @param {string} conversationId - The conversation/room ID
   * @returns {Object|null} Draft data or null if not found/expired
   */
  getDraft(conversationId) {
    if (!this.storageAvailable) {
      return null;
    }

    if (!conversationId) {
      return null;
    }

    try {
      const storageKey = this.getStorageKey(conversationId);
      const storedData = sessionStorage.getItem(storageKey);

      if (!storedData) {
        return null;
      }

      const draft = JSON.parse(storedData);

      // Check if draft is expired
      if (draft.timestamp && (Date.now() - draft.timestamp > this.maxAge)) {
        logger.debug(`DraftStorage: Draft expired for ${conversationId}`);
        this.clearDraft(conversationId);
        return null;
      }

      logger.debug(`DraftStorage: Retrieved draft for ${conversationId}`, draft);
      return draft;
    } catch (e) {
      logger.error('DraftStorage: Error retrieving draft:', e);
      return null;
    }
  }

  /**
   * Clear draft for a conversation
   * @param {string} conversationId - The conversation/room ID
   * @returns {boolean} Success status
   */
  clearDraft(conversationId) {
    if (!this.storageAvailable) {
      return false;
    }

    if (!conversationId) {
      return false;
    }

    try {
      const storageKey = this.getStorageKey(conversationId);
      sessionStorage.removeItem(storageKey);
      logger.debug(`DraftStorage: Cleared draft for ${conversationId}`);
      return true;
    } catch (e) {
      logger.error('DraftStorage: Error clearing draft:', e);
      return false;
    }
  }

  /**
   * Get all drafts (for restoration on app load)
   * @returns {Object} Map of conversationId -> draft data
   */
  getAllDrafts() {
    if (!this.storageAvailable) {
      return {};
    }

    const drafts = {};

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        // Only process our draft keys
        if (key && key.startsWith(this.storageKeyPrefix + '_')) {
          const conversationId = key.substring(this.storageKeyPrefix.length + 1);
          const draft = this.getDraft(conversationId);
          
          if (draft) {
            drafts[conversationId] = draft;
          }
        }
      }

      logger.debug(`DraftStorage: Retrieved ${Object.keys(drafts).length} drafts`);
      return drafts;
    } catch (e) {
      logger.error('DraftStorage: Error getting all drafts:', e);
      return {};
    }
  }

  /**
   * Cleanup old drafts (older than maxAge)
   * @returns {number} Number of drafts cleaned up
   */
  cleanupOldDrafts() {
    if (!this.storageAvailable) {
      return 0;
    }

    let cleanedCount = 0;

    try {
      const keysToRemove = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        if (key && key.startsWith(this.storageKeyPrefix + '_')) {
          try {
            const storedData = sessionStorage.getItem(key);
            const draft = JSON.parse(storedData);

            // Check if expired
            if (draft.timestamp && (Date.now() - draft.timestamp > this.maxAge)) {
              keysToRemove.push(key);
            }
          } catch {
            // Invalid JSON, mark for removal
            keysToRemove.push(key);
          }
        }
      }

      // Remove expired drafts
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        cleanedCount++;
      });

      if (cleanedCount > 0) {
        logger.info(`DraftStorage: Cleaned up ${cleanedCount} old drafts`);
      }

      return cleanedCount;
    } catch (e) {
      logger.error('DraftStorage: Error cleaning up old drafts:', e);
      return cleanedCount;
    }
  }

  /**
   * Clear all drafts (useful for logout)
   * @returns {number} Number of drafts cleared
   */
  clearAllDrafts() {
    if (!this.storageAvailable) {
      return 0;
    }

    let clearedCount = 0;

    try {
      const keysToRemove = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        if (key && key.startsWith(this.storageKeyPrefix + '_')) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        clearedCount++;
      });

      logger.info(`DraftStorage: Cleared all ${clearedCount} drafts`);
      return clearedCount;
    } catch (e) {
      logger.error('DraftStorage: Error clearing all drafts:', e);
      return clearedCount;
    }
  }
}

// Export singleton instance
const draftStorageService = new DraftStorageService();
export default draftStorageService;
